// Story 7-FU-I (ADO-1222) — the depot-scoped READ for depot-stamped cache entries.
//
// The per-SKU caches (`useSubstitutesStore`, `useRecommendationsStore`) are keyed by SKU
// ALONE and stamp the depot they were fetched at on each entry. Keying by SKU is what lets
// several surfaces share one fetch; the stamp is what makes an entry checkable. The gap is
// the READ: nothing stopped a consumer from taking `bySku[sku]` straight, and a consumer
// that forgets the compare renders the PREVIOUS depot's data until the refetch lands.
//
// That gap is not hypothetical and not one-off. A depot switch does not always reset the
// caches (`useSessionDepotId` documents the paths that don't), and the same miss has now
// been found in four consumers across three surfaces. So the compare lives HERE, in one
// named function every consumer routes through, rather than being re-derived per call site:
// a stale entry is indistinguishable from a correct one at a glance, which is exactly the
// kind of check that does not survive the next refactor when it is copy-pasted.

/** A cache entry that records the depot it was fetched at (`null` = no active depot). */
export interface DepotStamped {
  readonly depotId: string | null;
}

/**
 * `entry` if it was fetched at `depotId`, otherwise `undefined`.
 *
 * A depot-mismatched entry is treated as NOT-YET-LOADED rather than as data, so a consumer
 * renders its empty/pending state through the refetch instead of the wrong depot's content.
 * `undefined` in, `undefined` out — an absent entry and a stale one are the same answer to
 * "what do we hold for this depot?", which is what keeps the call sites branch-free.
 *
 * (`depotId === null` — no active depot — matches an entry stamped `null`: that is the
 * graceful-degrade state, not a mismatch.)
 */
export function atDepot<T extends DepotStamped>(
  entry: T | undefined,
  depotId: string | null,
): T | undefined {
  return entry !== undefined && entry.depotId === depotId ? entry : undefined;
}
